import { useRef, useCallback, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";

// Worker served from public/ to avoid webpack bundling issues with CRA
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

interface PdfRendererState {
  pdfDoc: pdfjsLib.PDFDocumentProxy | null;
  currentFile: string | null;
}

interface RenderOptions {
  zoom?: number;
  maxCanvasSide?: number;
  containerWidth?: number;
}

export const usePdfRenderer = () => {
  const stateRef = useRef<PdfRendererState>({
    pdfDoc: null,
    currentFile: null,
  });
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  const loadPdf = useCallback(
    async (url: string): Promise<pdfjsLib.PDFDocumentProxy | null> => {
      if (stateRef.current.currentFile === url && stateRef.current.pdfDoc) {
        return stateRef.current.pdfDoc;
      }
      try {
        const doc = await pdfjsLib.getDocument(url).promise;
        stateRef.current.pdfDoc = doc;
        stateRef.current.currentFile = url;
        return doc;
      } catch (err) {
        console.error("Failed to load PDF:", err);
        return null;
      }
    },
    [],
  );

  const renderPage = useCallback(
    async (
      canvas: HTMLCanvasElement,
      url: string,
      pageNum: number,
      opts: RenderOptions = {},
    ): Promise<{ width: number; height: number; scale: number } | null> => {
      const doc = await loadPdf(url);
      if (!doc) return null;

      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
          await renderTaskRef.current.promise;
        } catch {
          /* ignore cancelled render */
        }
        renderTaskRef.current = null;
      }

      try {
        const page = await doc.getPage(pageNum);
        const zoom = opts.zoom || 1;
        const pixelRatio = window.devicePixelRatio || 1;
        const maxSide = opts.maxCanvasSide || 16384;

        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth =
          opts.containerWidth || canvas.parentElement?.clientWidth || 800;
        const baseScale = containerWidth / baseViewport.width;
        const effectiveScale = baseScale * zoom * pixelRatio;

        let canvasW = Math.round(
          baseViewport.width * baseScale * zoom * pixelRatio,
        );
        let canvasH = Math.round(
          baseViewport.height * baseScale * zoom * pixelRatio,
        );
        let renderScale = effectiveScale;

        if (canvasW > maxSide || canvasH > maxSide) {
          const factor = maxSide / Math.max(canvasW, canvasH);
          canvasW = Math.round(canvasW * factor);
          canvasH = Math.round(canvasH * factor);
          renderScale = effectiveScale * factor;
        }

        canvas.width = canvasW;
        canvas.height = canvasH;

        const cssW = baseViewport.width * baseScale * zoom;
        const cssH = baseViewport.height * baseScale * zoom;
        canvas.style.width = `${cssW}px`;
        canvas.style.height = `${cssH}px`;

        const viewport = page.getViewport({ scale: renderScale });
        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, canvasW, canvasH);

        const renderTask = page.render({
          canvasContext: ctx,
          canvas,
          viewport,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        renderTaskRef.current = null;

        return { width: cssW, height: cssH, scale: baseScale };
      } catch (err: unknown) {
        if (
          err instanceof Error &&
          err.message?.includes("Rendering cancelled")
        ) {
          return null;
        }
        console.error("Failed to render page:", err);
        return null;
      }
    },
    [loadPdf],
  );

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return { loadPdf, renderPage };
};
